import { useEffect, useId } from 'react';
import { requestWork, retryWorkEvaluation } from '@/lib/work-actions';
import { TaskActionQuestion } from './TaskActionQuestion';
import { ActivityForgeHandoffDialog } from '@/components/activity/ActivityForgeHandoffDialog';
import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { WorkActionPresentation } from '@shared/types/work-action';
import { Button } from '@/components/ui/Button';
import { ViewActionButton } from '@/components/views/atoms/ViewActionButton';
import type { AccountTaskActionResponse } from '@shared/types/task-actions';
import { invokeTaskAction, prepareAccountTaskPlan } from '@/lib/task-action';
import { useOptionalAppAssistant } from '@/lib/app-assistant-context';
import { ACCOUNT_TASK_COMPLETED_EVENT } from '@/components/activity/AccountCapabilityLauncher';
import { buildContextualToolPrefill } from '@/lib/contextual-tool-prefill';
import { cn } from '@/lib/utils';
import { sweepBusinessVocabulary } from '@/lib/ui-editorial-sweeper';

export const DENSE_ACTION_CLASS =
  'border border-domino-sand bg-domino-lilac text-domino-aubergine hover:bg-domino-sunSoft/60 focus:ring-domino-prune';

export const WorkActionControl = ({
  work,
  showAdjustment = true,
  dense = false,
  label,
}: {
  work: WorkActionPresentation;
  showAdjustment?: boolean;
  dense?: boolean;
  /** Short verb shown instead of the command label (dense lists); the full
   * label stays available as the tooltip. */
  label?: string;
}) => {
  const controlClass = dense
    ? 'h-auto min-h-8 max-w-full px-3 py-1 text-xs leading-4 shadow-none'
    : undefined;
  const denseActionClass = dense ? DENSE_ACTION_CLASS : undefined;
  const navigate = useNavigate();
  const [forgeOpen, setForgeOpen] = useState(false);
  const assistant = useOptionalAppAssistant();
  const pendingRef = useRef(false);
  const [pending, setPending] = useState(false);
  const [needsConnection, setNeedsConnection] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const dialogTitleId = useId();
  const [feedback, setFeedback] = useState<AccountTaskActionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const command = work.command;
  useEffect(() => {
    if (error || feedback) dialog.current?.showModal();
    else if (dialog.current?.open) dialog.current.close();
  }, [error, feedback]);
  const refresh = async () => {
    if (pendingRef.current) return;
    pendingRef.current = true; setPending(true); setError(null);
    try {
      if (command.kind === 'refresh' && command.retry && work.context.topicId) {
        await retryWorkEvaluation(work.context.topicId);
      } else {
        const result = await requestWork(work.context, null, crypto.randomUUID());
        assistant?.openWork({ ...work.context, ...result });
      }
    } catch (cause) { setError(cause instanceof Error ? cause.message : 'La demande est conservée. Actualisez le compte avant de reprendre.'); }
    finally { pendingRef.current = false; setPending(false); window.dispatchEvent(new CustomEvent(ACCOUNT_TASK_COMPLETED_EVENT)); }
  };
  const prepare = async () => {
    if (command.kind !== 'prepare_plan' || pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setError(null);
    try {
      const result = await prepareAccountTaskPlan({
        operation: 'prepare_plan',
        sourceRunId: command.sourceRunId,
      });
      if (result.status === 'failed' || result.status === 'unavailable') setError(result.message);
      if (result.status === 'running') setFeedback({ status:'running', taskRunId:null, message:result.message, viewEnvelope:null });
      window.dispatchEvent(
        new CustomEvent(ACCOUNT_TASK_COMPLETED_EVENT, {
          detail: { sourceRunId: command.sourceRunId },
        })
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'La préparation a échoué.');
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };
  const executeProposal = async (clarification?: { key: string; value: string }) => {
    if (command.kind !== 'task_proposal' || pendingRef.current) return;
    setFeedback(null);
    pendingRef.current = true;
    setPending(true);
    setError(null);
    try {
      const data = (await invokeTaskAction({
        operation: 'execute_proposal',
        proposalId: command.proposalId,
        topicId: command.topicId,
        expectedVersion: command.version, clarification,
      })) as AccountTaskActionResponse;
      if (data?.status === 'completed' && data.artifactLocation?.kind === 'inline_thread') {
        const query = new URLSearchParams({
          topic: command.topicId,
          result: data.taskRunId,
        });
        assistant?.openWork(work.context);
        navigate(
          `/threads/${encodeURIComponent(data.artifactLocation.subjectId)}?${query.toString()}`
        );
      } else {
        setFeedback(data);
        setNeedsConnection(data.reasonCodes?.includes('connection_required') ?? false);
      }
      window.dispatchEvent(new CustomEvent(ACCOUNT_TASK_COMPLETED_EVENT));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Ce travail n’a pas abouti.');
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };
  if (command.kind === 'none') return work.notice ? <span role="status" className="text-xs text-domino-inkSoft">{work.context.preparation?.status === 'queued' || work.context.preparation?.status === 'running' || work.notice === 'Préparation en cours…' ? 'Préparation en cours…' : 'Actions indisponibles'}</span> : null;
  if (command.kind === 'execute')
    return (
      <ViewActionButton
        action={command.action}
        className={cn(controlClass, denseActionClass)}
      />
    );
  if (command.kind === 'resume')
    return (
      <Link
        className="text-sm font-semibold text-domino-aubergine underline underline-offset-4"
        to={command.href}
        title={label ? sweepBusinessVocabulary(command.label) : undefined}
        onClick={() => assistant?.openWork(work.context)}
      >
        {label ?? sweepBusinessVocabulary(command.label)}
      </Link>
    );
  if (command.kind === 'tool_proposal')
    return (
      <Link
        className={cn(
          'inline-flex items-center rounded-lg bg-domino-aubergine px-4 py-2.5 text-sm font-semibold text-white hover:bg-domino-prune',
          controlClass,
          denseActionClass
        )}
        to={`/tools/${encodeURIComponent(command.toolKey)}`}
        title={label ? sweepBusinessVocabulary(command.label) : undefined}
        state={{
          toolPrefill: {
            ...(command.prefill ?? {}),
            ...buildContextualToolPrefill(command.toolKey, work),
          },
          threadProposalHandoff: {
            proposalId: command.proposalId,
            topicId: command.topicId,
            expectedTopicVersion: command.version,
            topicTitle: work.title,
            subjectId: work.context.subjectId,
            accountSubjectId: work.context.decisionFocus?.accountSubjectId ?? null,
          },
        }}
      >
        {label ?? sweepBusinessVocabulary(command.label)}
      </Link>
    );
  return (
    <div className="space-y-2">
      <ActivityForgeHandoffDialog
        onClose={() => setForgeOpen(false)}
        selection={
          forgeOpen
            ? {
                actionLabel: work.title,
                summary: work.reason,
                seedPrompt: `${work.title}\n${work.reason ?? ''}\nCompte de référence : ${work.context.subjectLabel}`,
                origin:
                  command.kind === 'forge'
                    ? {
                        proposalId: command.proposalId,
                        topicId: command.topicId,
                        expectedVersion: command.version,
                      }
                    : null,
              }
            : null
        }
      />
      <Button
        variant={dense ? 'secondary' : 'primary'}
        className={cn(controlClass, denseActionClass)}
        disabled={pending}
        title={label ? sweepBusinessVocabulary(command.label) : undefined}
        onClick={() =>
          command.kind === 'refresh' ? void refresh() : command.kind === 'prepare_plan'
            ? void prepare()
            : command.kind === 'task_proposal'
              ? void executeProposal()
              : command.kind === 'forge'
                ? setForgeOpen(true)
                : assistant?.openWork(work.context)
        }
      >
        {pending ? 'Préparation…' : label ?? sweepBusinessVocabulary(command.label)}
      </Button>
      {showAdjustment && command.kind === 'prepare_plan' && assistant && (
        <Button variant="ghost" onClick={() => assistant.openWork(work.context)}>
          Préciser ce travail
        </Button>
      )}
      {(error || feedback) && <dialog ref={dialog} aria-labelledby={dialogTitleId} onClose={() => { setError(null); setFeedback(null); }} className="w-[min(440px,calc(100vw-32px))] rounded-xl border border-domino-cardBorder bg-white p-5 shadow-xl backdrop:bg-black/20">
        <h2 id={dialogTitleId} className="mb-4 text-base font-medium">{work.title}</h2>
        {feedback?.status === 'needs_input' ? <TaskActionQuestion question={feedback.question} onAnswer={answer => void executeProposal(answer)} /> : <p role={feedback?.status === 'running' ? 'status' : 'alert'} className="text-sm leading-6">{error ?? feedback?.message}</p>}
        {needsConnection && <Link to="/settings" onClick={() => { assistant?.openWork(work.context); dialog.current?.close(); }} className="mt-3 block text-sm underline">Configurer la connexion</Link>}
        <div className="mt-5 flex justify-end gap-2"><Button variant="ghost" onClick={() => dialog.current?.close()}>Fermer</Button>
          {feedback?.status !== 'needs_input' && assistant && <Button variant="secondary" onClick={() => { assistant.openWork(work.context); dialog.current?.close(); }}>Reprendre dans le compte</Button>}
        </div>
      </dialog>}
    </div>
  );
};
